"use client"

import { useMemo, useState } from "react"
import {
  Button,
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@ui/shad"
import { cn } from "@ui/utils"
import { Plus, X } from "lucide-react"

import { useFetchUsers } from "@/hooks/api/users"
import type { EmailRecipientType, TeamMemberRecipientsConfig } from "@/components/automations/types"

interface TeamMemberRecipientsFieldProps {
  value: TeamMemberRecipientsConfig
  onChange: (value: TeamMemberRecipientsConfig) => void
  controlWidthClassName?: string
}

const RECIPIENT_TYPES: { value: EmailRecipientType; label: string }[] = [
  { value: "to", label: "To" },
  { value: "cc", label: "CC" },
  { value: "bcc", label: "BCC" },
  { value: "none", label: "None" },
]

export function TeamMemberRecipientsField({
  value,
  onChange,
  controlWidthClassName = "w-24",
}: TeamMemberRecipientsFieldProps) {
  const [open, setOpen] = useState(false)
  const { data } = useFetchUsers()
  const users = useMemo(() => data?.users ?? [], [data?.users])
  const usersById = useMemo(() => new Map(users.map((user) => [user.id, user])), [users])
  const availableUsers = useMemo(() => users.filter((user) => value[user.id] === undefined), [users, value])

  const addTeamMember = (userId: string) => {
    onChange({ ...value, [userId]: "cc" })
    setOpen(false)
  }

  const updateRecipientType = (userId: string, recipientType: EmailRecipientType) => {
    onChange({ ...value, [userId]: recipientType })
  }

  const removeTeamMember = (userId: string) => {
    const nextValue = { ...value }
    delete nextValue[userId]
    onChange(nextValue)
  }

  return (
    <div className="space-y-2">
      {Object.entries(value).map(([userId, recipientType]) => {
        const user = usersById.get(userId)

        return (
          <div key={userId} className="flex items-center justify-between gap-2">
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-medium">{user?.full_name || "Unknown team member"}</div>
              {user?.email && <div className="text-muted-foreground truncate text-xs">{user.email}</div>}
            </div>
            <div className={cn("flex shrink-0 items-center gap-1", controlWidthClassName)}>
              <Select
                value={recipientType}
                onValueChange={(nextType) => updateRecipientType(userId, nextType as EmailRecipientType)}
              >
                <SelectTrigger size="sm" className="min-w-0 flex-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {RECIPIENT_TYPES.map((recipientTypeOption) => (
                    <SelectItem key={recipientTypeOption.value} value={recipientTypeOption.value}>
                      {recipientTypeOption.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="size-7 shrink-0"
                aria-label={`Remove ${user?.full_name || "team member"}`}
                onClick={() => removeTeamMember(userId)}
              >
                <X />
              </Button>
            </div>
          </div>
        )
      })}

      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button type="button" variant="outline" size="sm">
            <Plus />
            Add team member
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-72 p-0" align="start">
          <Command>
            <CommandInput placeholder="Search team members..." />
            <CommandList>
              <CommandEmpty>No team members found.</CommandEmpty>
              {availableUsers.map((user) => (
                <CommandItem
                  key={user.id}
                  value={`${user.full_name} ${user.email}`}
                  onSelect={() => addTeamMember(user.id)}
                >
                  <div className="min-w-0">
                    <div className="truncate text-sm font-medium">{user.full_name}</div>
                    <div className="text-muted-foreground truncate text-xs">{user.email}</div>
                  </div>
                </CommandItem>
              ))}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  )
}
